import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';
import { authApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import AppLogo from '@/components/AppLogo';

const loginSchema = z.object({
  tenantSlug: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,29}$/, 'Use format: cityhosp-nagpur or tenant_demo')
               .optional().or(z.literal('')),
  email:      z.string().email('Invalid email'),
  password:   z.string().min(6, 'Password must be at least 6 characters'),
});

const mfaSchema = z.object({
  code: z.string().length(6, 'Code must be exactly 6 digits').regex(/^\d+$/, 'Digits only'),
});

const recoverySchema = z.object({
  code: z.string().regex(/^[A-Fa-f0-9]{6}-[A-Fa-f0-9]{6}$/, 'Format: XXXXXX-XXXXXX'),
});

type LoginForm    = z.infer<typeof loginSchema>;
type MfaForm      = z.infer<typeof mfaSchema>;
type RecoveryForm = z.infer<typeof recoverySchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth, setMfaPending, setMfaSetupPending, requiresMfa, mfaTempKey } = useAuthStore();
  const [showPassword, setShowPassword]   = useState(false);
  const [useRecovery, setUseRecovery]     = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [loading, setLoading]             = useState(false);

  const loginForm    = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });
  const mfaForm      = useForm<MfaForm>({ resolver: zodResolver(mfaSchema) });
  const recoveryForm = useForm<RecoveryForm>({ resolver: zodResolver(recoverySchema) });

  async function onLogin(values: LoginForm) {
    setError(null);
    setLoading(true);
    try {
      const slug     = values.tenantSlug?.trim() || undefined;
      const { data } = await authApi.login(values.email, values.password, slug);
      if (data.requiresMfaSetup) {
        setMfaSetupPending(data.tempKey);
        navigate('/mfa/setup');
      } else if (data.requiresMfa) {
        setMfaPending(data.tempKey);
      } else {
        setAuth(data.user, data.accessToken, data.refreshToken);
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function onMfa(values: MfaForm) {
    if (!mfaTempKey) return;
    setError(null);
    setLoading(true);
    try {
      const { data } = await authApi.mfaVerify(values.code, mfaTempKey);
      setAuth(data.user, data.accessToken, data.refreshToken);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Invalid MFA code');
    } finally {
      setLoading(false);
    }
  }

  async function onRecovery(values: RecoveryForm) {
    if (!mfaTempKey) return;
    setError(null);
    setLoading(true);
    try {
      const { data } = await authApi.mfaRecovery(values.code.toUpperCase(), mfaTempKey);
      setAuth(data.user, data.accessToken, data.refreshToken);
      if (data.warning) setError(data.warning); // show low-codes warning after login
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Invalid recovery code');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Left — branding panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
           style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <AppLogo size={28} inverted />
          </div>
          <div>
            <div className="text-white font-semibold text-lg leading-tight">Aarogya Saarthi</div>
            <div className="text-white/40 text-xs tracking-widest uppercase">India's Nursing Home Software</div>
          </div>
        </div>

        <div>
          <h1 className="text-white font-bold text-4xl leading-tight mb-4">
            Trusted by hospitals<br />
            <span className="text-orange">across India</span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed max-w-sm">
            DPDP-compliant, ABDM-connected hospital management — designed for the realities of Tier 2 and 3 cities.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[['OPD', 'Queue & Scheduling'], ['IPD', 'Bed Management'], ['Billing', 'GST Compliant']].map(([t, s]) => (
            <div key={t} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-orange font-semibold text-sm">{t}</div>
              <div className="text-white/50 text-xs mt-1">{s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — login / MFA form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-card-md p-8">

            {/* ── Login form ────────────────────────────────────── */}
            {!requiresMfa ? (
              <>
                <h2 className="text-navy text-2xl font-semibold mb-1">Welcome back</h2>
                <p className="text-gray-400 text-sm mb-8">Sign in to your hospital account</p>

                <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">
                      Hospital ID
                      <span className="text-gray-400 font-normal text-xs ml-1">(leave blank for BBS Admin)</span>
                    </label>
                    <input
                      {...loginForm.register('tenantSlug')}
                      placeholder="cityhosp-nagpur"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange
                                 placeholder:text-gray-300"
                    />
                    {loginForm.formState.errors.tenantSlug && (
                      <p className="text-clinical-danger text-xs mt-1">
                        {loginForm.formState.errors.tenantSlug.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Email</label>
                    <input
                      {...loginForm.register('email')}
                      type="email"
                      placeholder="doctor@hospital.in"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-clinical-danger text-xs mt-1">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        {...loginForm.register('password')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-200 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                      />
                      <button type="button"
                        onClick={() => setShowPassword(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy">
                        {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-clinical-danger text-xs mt-1">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm
                                    rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                               hover:bg-orange-dark transition-colors disabled:opacity-50
                               flex items-center justify-center gap-2">
                    {loading && <Loader2 size={16} className="animate-spin"/>}
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
              </>

            ) : !useRecovery ? (
              /* ── TOTP form ────────────────────────────────────── */
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                    <ShieldCheck size={20} className="text-orange"/>
                  </div>
                  <div>
                    <h2 className="text-navy text-xl font-semibold">Two-factor authentication</h2>
                    <p className="text-gray-400 text-sm">Open Google Authenticator for your 6-digit code</p>
                  </div>
                </div>

                <form onSubmit={mfaForm.handleSubmit(onMfa)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Authentication code</label>
                    <input
                      {...mfaForm.register('code')}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      autoFocus
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-center
                                 text-2xl font-mono tracking-[0.5em]
                                 focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                    />
                    {mfaForm.formState.errors.code && (
                      <p className="text-clinical-danger text-xs mt-1 text-center">
                        {mfaForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm
                                    rounded-lg px-4 py-3 text-center">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                               hover:bg-orange-dark transition-colors disabled:opacity-50
                               flex items-center justify-center gap-2">
                    {loading && <Loader2 size={16} className="animate-spin"/>}
                    {loading ? 'Verifying…' : 'Verify code'}
                  </button>
                </form>

                <button
                  onClick={() => { setUseRecovery(true); setError(null); }}
                  className="mt-4 w-full text-center text-xs text-gray-400 hover:text-navy transition-colors">
                  Lost access to your authenticator? Use a recovery code
                </button>
              </>

            ) : (
              /* ── Recovery code form ───────────────────────────── */
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-orange/10 flex items-center justify-center">
                    <ShieldCheck size={20} className="text-orange"/>
                  </div>
                  <div>
                    <h2 className="text-navy text-xl font-semibold">Recovery code</h2>
                    <p className="text-gray-400 text-sm">Enter one of your saved recovery codes</p>
                  </div>
                </div>

                <form onSubmit={recoveryForm.handleSubmit(onRecovery)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1.5">Recovery code</label>
                    <input
                      {...recoveryForm.register('code')}
                      type="text"
                      placeholder="XXXXXX-XXXXXX"
                      autoFocus
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-center
                                 text-lg font-mono tracking-widest uppercase
                                 focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
                    />
                    {recoveryForm.formState.errors.code && (
                      <p className="text-clinical-danger text-xs mt-1 text-center">
                        {recoveryForm.formState.errors.code.message}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm
                                    rounded-lg px-4 py-3 text-center">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                               hover:bg-orange-dark transition-colors disabled:opacity-50
                               flex items-center justify-center gap-2">
                    {loading && <Loader2 size={16} className="animate-spin"/>}
                    {loading ? 'Verifying…' : 'Use recovery code'}
                  </button>
                </form>

                <button
                  onClick={() => { setUseRecovery(false); setError(null); }}
                  className="mt-4 w-full text-center text-xs text-gray-400 hover:text-navy transition-colors">
                  ← Back to authenticator code
                </button>
              </>
            )}

            <p className="text-center text-xs text-gray-400 mt-6">
              Protected by TOTP (RFC 6238) · DPDP Act 2023 compliant
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
